import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { AccountsService } from '../services/accounts.service';
import { AuthService } from '../services/auth.service';
import { Session, accountInfo } from '../services/session.service';

/** Where a locked-out account is sent, and the one route this guard lets be. */
export const BILLING_LOCKED_PATH = 'billing-locked';

/**
 * The reasons this app knows how to render.
 *
 * 🔴 The guard and the shell MUST agree on this set. The guard used to divert on
 * any truthy reason while the shell dropped anything outside these three — so a
 * future server-side value produced an unbreakable loop: guard diverts, shell
 * says "your account is available", Continue goes to /home, guard diverts again,
 * while every API call 402s underneath. One list, imported by both.
 */
export const KNOWN_ENTITLEMENT_REASONS = ['trial_expired', 'past_due', 'canceled'] as const;
export type EntitlementReason = (typeof KNOWN_ENTITLEMENT_REASONS)[number];

export const isKnownEntitlementReason = (value: unknown): value is EntitlementReason =>
  typeof value === 'string' &&
  (KNOWN_ENTITLEMENT_REASONS as readonly string[]).includes(value);

/**
 * Divert a locked-out account to the billing-only shell (WEA-1141).
 *
 * 🔴 THIS IS PRESENTATION, NOT ENFORCEMENT. `MembershipEntitlementGuard`
 * (WEA-1140) is what actually refuses the request, globally, with a 402. Without
 * this guard the customer still cannot use the product — they just experience it
 * as a screen full of errors instead of a payment prompt. `nest/CLAUDE.md`:
 * whatever the FE gates on, the BE must enforce. It does; this only decides what
 * the customer looks at.
 *
 * So a route that forgets this guard degrades to "a wall of 402s", not to a
 * security hole. That is why the enforcement lives server-side and why the route
 * list is pinned by a spec rather than trusted to memory.
 *
 * 🔴 Reads `entitlementReason`, never `membershipStatus`. The two are not the
 * same question: a `past_due` account inside its 14-day grace is fine, a
 * `canceled` one inside its paid period is fine. `entitlementReason` is computed
 * by the server's own `entitlementFailure`, so this can never disagree with what
 * the API is actually doing.
 *
 * Fails OPEN on anything unexpected — an error, a missing field, or a reason
 * this build does not recognise (see `isKnownEntitlementReason`; the shell drops
 * the same set, and disagreeing produced a redirect loop). A legacy Contensive account has no subscription row and
 * must never see this shell, and neither must anyone whose status simply could
 * not be read.
 *
 * 🔴 Waits for the session before doing anything (WAM-755). Angular runs a
 * route's guards concurrently, so on a fresh tab this runs while AuthGuard is
 * still loading the session. Reading `session.getUser()` synchronously built an
 * empty identity key (`|`), cached the status under it and fired the request
 * unauthenticated. So with no user yet we wait on the same signal WeaveGuard
 * uses (`AuthService.getAuthObservable()`, filtered on an actual user because the
 * subject can hold a stale `true`), and only then build the key and ask for the
 * status. If AuthGuard fails the navigation the router unsubscribes, so the wait
 * never hangs. Denial is a UrlTree, not `router.navigate`.
 */
export const BillingLockGuard: CanActivateFn = () => {
  const accounts = inject(AccountsService);
  const session = inject(Session);
  const authService = inject(AuthService);
  const router = inject(Router);

  const check = () => {
    const user = session.getUser() as
      | { userRequestKey?: string; userData?: { currentUserAccount?: accountInfo } }
      | null;
    const accountKey = user?.userData?.currentUserAccount?.accountRequestKey ?? '';
    // Byte-identical to HeaderAccountInfoComponent / RightMenuComponent /
    // PendoService, so this is a shared cache hit rather than another request on
    // every navigation (WEA-1136).
    const identityKey = `${accountKey}|${user?.userRequestKey ?? ''}`;

    return accounts.getAccountStatusCached(identityKey).pipe(
      first(),
      map((resp) => {
        const data = resp?.success ? resp.data?.[0]?.dataSet : null;
        const reason = data?.entitlementReason ?? null;
        // Unknown values fail OPEN, matching this guard's docblock — and matching
        // the shell, which can only render the three it knows.
        if (!isKnownEntitlementReason(reason)) return true;
        return router.createUrlTree([`/${BILLING_LOCKED_PATH}`]);
      }),
      catchError(() => of(true)),
    );
  };

  if (session.getUser()) {
    return check();
  }

  const sessionLoaded$: Observable<boolean> = authService.getAuthObservable().pipe(
    // The subject can hold a stale `true` while storage is empty, so also
    // require an actual user before resolving (same as WeaveGuard).
    filter((loggedIn) => loggedIn && !!session.getUser()),
    first(),
  );
  return sessionLoaded$.pipe(switchMap(() => check()));
};
